import {useCallback, useEffect, useRef, useState, useMemo} from 'react';
import {useDispatch} from 'react-redux';
import {userInfo} from '../../api/Authentication';
import {withRetryOnTransientNetworkFailure} from '../../api/retryTransientNetwork';
import {
  IdleManagerConfig,
  IdleManagerState,
  UseIdleManagerResult,
} from './types';
import {setUserDetailsGA} from '../../redux/slices/analyticsSlice';
import {getGAUserDetailsFromEmail} from '../../utils/analyticsUtils';
import { initQualtrics } from '../../utils/qualtrics';

const DEFAULT_CONFIG: Required<IdleManagerConfig> = {
  checkInterval: 5 * 60 * 1000, // 5 minutes
  maxIdleTime: 3 * 60 * 60 * 1000, // 3 hours
  gracePeriod: 2 * 60 * 1000, // 2 minutes
  debug: process.env.NODE_ENV === 'development', //true in development, false in production
};

/**
 * Smart idle manager hook that handles tab sleep detection and authentication checks
 *
 * Features:
 * - Periodic userInfo calls to maintain session
 * - Tab sleep detection using timestamp comparison
 * - Smart wake-up behavior (userInfo vs logout based on elapsed time)
 * - Configurable intervals and timeouts
 *
 * @param config Configuration options for the idle manager
 * @returns Idle manager state and control functions
 */
export const useIdleManager = (
  config: IdleManagerConfig = {}
): UseIdleManagerResult => {
  const mergedConfig = useMemo(
    () => ({...DEFAULT_CONFIG, ...config}),
    [config]
  );
  const dispatch = useDispatch();

  const [state, setState] = useState<IdleManagerState>({
    isActive: false,
    isAuthenticated: null,
    lastError: null,
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  // Ref instead of state — updating this must never trigger a re-render
  const lastUserActivityRef = useRef<number>(Date.now());
  const qualtricsSyncedRef = useRef(false);

  /**
   * Stable interval callback for periodic userInfo checks
   */
  const performCheck = useCallback(async (): Promise<void> => {
    try {
      if (mergedConfig.debug) {
        console.log('[IdleManager] Performing userInfo check...', {
          intervalRef,
          mergedConfig,
        });
      }

      const userData = await withRetryOnTransientNetworkFailure(() => userInfo());
      if (userData?.data?.user?.email) {
        const gaUserInfo = await getGAUserDetailsFromEmail(
          userData.data.user.email
        );
        dispatch(setUserDetailsGA(gaUserInfo));
        if (!qualtricsSyncedRef.current && gaUserInfo.id) {
          initQualtrics(gaUserInfo.id)
          qualtricsSyncedRef.current = true; // Mark as done
        }
      }

      setState((prev) => ({
        ...prev,
        isAuthenticated: true,
        lastError: null,
      }));

      if (mergedConfig.debug) {
        console.log('[IdleManager] userInfo check successful', {
          intervalRef,
          mergedConfig,
        });
      }
    } catch (error) {
      const errorObj =
        error instanceof Error ? error : new Error(String(error));
      if (mergedConfig.debug) {
        console.log('[IdleManager] userInfo check failed:', errorObj);
      }

      setState((prev) => ({
        ...prev,
        isAuthenticated: false,
        lastError: errorObj,
      }));
    }
  }, [mergedConfig.debug, dispatch]);

  /**
   * Start the idle manager
   */
  const start = useCallback((): void => {
    if (intervalRef.current) {
      if (mergedConfig.debug) {
        console.log('[IdleManager] IdleManager already active', {
          intervalRef,
          mergedConfig,
        });
      }
      return;
    }

    if (mergedConfig.debug) {
      console.log('[IdleManager] Starting IdleManager', {
        intervalRef,
        mergedConfig,
      });
    }

    setState((prev) => ({
      ...prev,
      isActive: true,
    }));

    performCheck();

    intervalRef.current = setInterval(performCheck, mergedConfig.checkInterval);

    if (mergedConfig.debug) {
      console.log('[IdleManager] IdleManager started');
    }
  }, [mergedConfig.debug, mergedConfig.checkInterval, performCheck]);

  /**
   * Stop the idle manager
   */
  const stop = useCallback((): void => {
    if (!intervalRef.current) {
      if (mergedConfig.debug) {
        console.log('[IdleManager] IdleManager already inactive');
      }
      return;
    }

    if (mergedConfig.debug) {
      console.log('[IdleManager] Stopping IdleManager');
    }

    clearInterval(intervalRef.current);
    intervalRef.current = null;

    setState((prev) => ({
      ...prev,
      isActive: false,
    }));

    if (mergedConfig.debug) {
      console.log('[IdleManager] IdleManager stopped');
    }
  }, [mergedConfig.debug]);

  /**
   * Force a userInfo check immediately
   */
  const checkNow = useCallback(async (): Promise<void> => {
    if (mergedConfig.debug) {
      console.log('[IdleManager] Forcing immediate userInfo check');
    }

    await performCheck();
  }, [mergedConfig.debug, performCheck]);

  /**
   * Reset the idle timer when user activity is detected.
   * Only updates refs — never calls setState — so callers do not re-render.
   */
  const resetTimer = useCallback((): void => {
    if (mergedConfig.debug) {
      console.log('[IdleManager] Resetting idle timer');
    }

    const now = Date.now();
    lastUserActivityRef.current = now;
  }, [mergedConfig.debug]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    ...state,
    start,
    stop,
    checkNow,
    resetTimer,
    lastUserActivityRef,
  };
};
